import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ProfileFieldView from "./profile-field-view";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import ProfileSubmitView from "./profile-submit-view";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useProfile from "@/hooks/use-profile";
import { ProfileSchema } from "@/schema";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import { Person, LocationOn, AccountCircle } from "@mui/icons-material";

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

const ProfileFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { registerUser, isLoading, error } = useProfile();
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [isNewUser] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();

  const form = useForm<z.infer<typeof ProfileSchema>>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      id: null,
      nickName: "",
      email: "",
      phoneNumber: "",
      role: "عميل",
      gender: null,
      accountType: null,
      region: "",
      city: "",
      identityType: "Citizen",
      identityNo: "",
      residenceNo: "",
    },
  });

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  useEffect(() => {
    const selectedRegion = form.watch("region");
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [form.watch("region")]);

  const onSubmit = async (values: z.infer<typeof ProfileSchema>) => {
    try {
      const submissionData = {
        ...values,
        identityNo:
          values.identityType === "Citizen" ? values.identityNo : null,
        residenceNo:
          values.identityType === "Resident" ? values.residenceNo : null,
      };

      await registerUser(submissionData);
      navigate("/confirmation");
    } catch (err) {
      console.error("Error during profile submission:", err);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={{ maxWidth: 800, mx: "auto", p: 2 }}>
          {/* Personal Information Section */}
          <Card
            elevation={2}
            sx={{
              boxShadow: "none",
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <Person color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="primary">
                  {t("profile.personalInfo") || "Personal Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <ProfileFieldView
                    name="nickName"
                    label={t("profile.fields.nickName")}
                    placeholder={t("profile.placeholders.enterNickName")}
                    formControl={form.control}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ProfileFieldView
                    name="email"
                    label={t("profile.fields.email")}
                    placeholder={t("profile.placeholders.enterEmail")}
                    formControl={form.control}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ProfileFieldView
                    name="phoneNumber"
                    label={t("profile.fields.phoneNumber")}
                    placeholder={t("profile.placeholders.enterPhoneNumber")}
                    formControl={form.control}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Location Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <LocationOn color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="secondary">
                  {t("company.fields.location") || "Location Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="region"
                    label={t("company.fields.region")}
                    placeholder={t("company.placeholders.selectRegion")}
                    inputType="combobox"
                    options={regions}
                    formControl={form.control}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="city"
                    label={t("company.fields.city")}
                    placeholder={t("company.placeholders.selectCity")}
                    inputType="combobox"
                    options={cities}
                    formControl={form.control}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Account Information Section */}
          <Card
            elevation={2}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  borderRadius: 2,
                }}
              >
                <AccountCircle color="success" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {t("profile.accountInfo") || "Account Information"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="gender"
                    label={t("profile.fields.gender")}
                    placeholder={t("profile.placeholders.selectGender")}
                    inputType="radio"
                    options={[
                      {
                        value: "Male",
                        label: t("profile.options.gender.male"),
                      },
                      {
                        value: "Female",
                        label: t("profile.options.gender.female"),
                      },
                    ]}
                    formControl={form.control}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ProfileFieldView
                    name="accountType"
                    label={t("profile.fields.accountType")}
                    placeholder={t("profile.placeholders.selectAccountType")}
                    inputType="radio"
                    options={[
                      {
                        value: "Individual",
                        label: t("profile.options.accountType.individual"),
                      },
                      {
                        value: "Establishment",
                        label: t("profile.options.accountType.establishment"),
                      },
                    ]}
                    formControl={form.control}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: 2,
              }}
            >
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Paper>
          )}

          {/* Submit Section */}
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <ProfileSubmitView isLoading={isLoading} isNewUser={isNewUser} />
            </CardContent>
          </Card>
        </Box>
      </form>
    </Form>
  );
};

export default ProfileFormView;
